import { useState } from "react";
import Text from "@core/text";
import { Button } from "@core/index";
import { useGetPerformanceTask } from "@packages/api/madlenAPI";
import { CheckCircle, Expand, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";
import { PerformanceTaskCard } from "@features/tools/components/performance-task/PerformanceTaskCard";
import { PerformanceTaskResponseDto } from "@packages/api/madlenAPI.schemas";
import SpinnerLoader from "../components/SpinnerLoader";

const PerformanceTaskDetailComponent = ({
  tool,
  closeModal,
}: {
  tool: PerformanceTaskResponseDto;
  closeModal: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <div
      className="fixed inset-0 z-[9999] overflow-hidden bg-black bg-opacity-75 dark:bg-opacity-90 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          closeModal();
        }
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <Text variant={"h1"}>
            {tool.title || t("performanceTasks.title")}
          </Text>
          <Button
            onClick={closeModal}
            variant="solid"
            size="icon"
            rounded="full"
            bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X size={24} className="text-gray-500 dark:text-gray-400" />
          </Button>
        </div>

        <div className="flex-1 overflow-auto p-6 modal-content">
          <PerformanceTaskCard
            courseName={tool.courseName}
            createdAt={tool.createdAt}
            description={tool.description}
            performanceCriteria={tool.performanceCriteria}
            materialsNeeded={tool.materialsNeeded}
            studentInstructions={tool.studentInstructions}
            gradeLevel={tool.gradeLevel}
            imageUrl={tool.imageUrl}
            isImageLoading={false}
            prompt={tool.title || tool.prompt}
            videoUrl={tool.videoUrl}
          />
        </div>
      </div>
    </div>
  );
};

const PerformanceTask = ({ id }: { id: number | null }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const { data, isLoading, isError } = useGetPerformanceTask(id, {
    query: {
      refetchInterval: (data) => {
        if (
          data?.state?.data?.performanceCriteria &&
          Array.isArray(data.state.data.performanceCriteria) &&
          data.state.data.performanceCriteria.length > 0
        ) {
          return false;
        }
        return 2000;
      },
      enabled: !!id,
      staleTime: Infinity,
      cacheTime: 1000 * 60 * 30,
    },
  });

  const isContentLoading =
    isLoading ||
    !data ||
    !data.studentInstructions ||
    ((!Array.isArray(data.performanceCriteria) ||
      data.performanceCriteria.length === 0) &&
      (!Array.isArray(data.materialsNeeded) ||
        data.materialsNeeded.length === 0));

  const handleExpandClick = () => {
    setExpanded(true);
  };

  const closeModal = () => {
    setExpanded(false);
  };

  if (isContentLoading) {
    return <SpinnerLoader />;
  }

  if (isError) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm mb-6">
        <h3 className="text-xl font-medium mb-4 text-red-500 dark:text-red-400">
          {t("performanceTasks.error.unableToLoad")}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
          {t("performanceTasks.error.tryRegenerate")}
        </p>
      </div>
    );
  }

  const { performanceCriteria = [], materialsNeeded = [] } = data || {};

  return (
    <>
      {expanded && data && (
        <PerformanceTaskDetailComponent
          tool={{
            ...data,
            gradeLevel: data.gradeLevel === -1 ? 0 : data.gradeLevel,
          }}
          closeModal={closeModal}
        />
      )}

      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm mb-6">
        <div className="flex w-full justify-between items-center">
          <h3 className="text-xl font-medium mb-4 flex items-center justify-between text-gray-900 dark:text-gray-100">
            {t("performanceTasks.title")}
          </h3>
          <button
            className="flex items-center justify-center p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition duration-200"
            onClick={handleExpandClick}
          >
            <Expand size={18} className="text-gray-400 dark:text-gray-500" />
          </button>
        </div>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
          {t("performanceTasks.subtitle")}
        </p>

        <div className="space-y-6">
          {Array.isArray(performanceCriteria) &&
            performanceCriteria.length > 0 && (
              <div className="bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-800 dark:to-blue-900/30 rounded-xl p-6">
                <h5 className="font-semibold text-lg mb-4 text-gray-900 dark:text-gray-100">
                  {t("performanceTasks.performanceCriteria")}
                </h5>
                <div className="prose dark:prose-invert max-w-none">
                  {performanceCriteria.slice(0, 1).map((criteria, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <div className="flex-1">
                        <Markdown className="prose dark:prose-invert font-semibold">
                          {criteria}
                        </Markdown>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {Array.isArray(materialsNeeded) && materialsNeeded.length > 0 && (
            <div className="bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-800 dark:to-blue-900/30 rounded-xl p-6">
              <h5 className="font-semibold text-lg mb-4 text-gray-900 dark:text-gray-100">
                {t("performanceTasks.materialsNeeded")}
              </h5>
              <ul className="space-y-2">
                {materialsNeeded.slice(0, 1).map((material, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <CheckCircle
                      size={20}
                      className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                    />
                    <Text
                      variant="span"
                      className="text-gray-700 dark:text-gray-300"
                    >
                      {material}
                    </Text>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PerformanceTask;
